import axios from 'axios';
import { getApiErrorMessage } from '../../lib/apiError';
import type { MfaChallengeResponse, MfaMethod } from '../../services/auth.service';

/**
 * A sign-in through another account (Google, Microsoft, Apple) that fails comes
 * back to the login page as a slug in the query string. Each known outcome gets
 * a sentence that says what happened and what to do next, and no sentence names
 * a particular account: the same slugs arrive whichever one was chosen.
 *
 * Kept in a Map, not an object literal: the slug is whatever the address bar
 * says, and `constructor` or `toString` must fall back like any unknown slug
 * instead of resolving to something inherited that the banner cannot render.
 */
const SIGN_IN_ERROR_COPY: ReadonlyMap<string, string> = new Map([
  ['oauth_failed', 'That sign-in did not complete. Please try again.'],
  ['invalid_state', 'That sign-in link is no longer valid. Please start again from this page.'],
  ['state_already_used', 'That sign-in link was already used. Please start again from this page.'],
  ['missing_params', 'That sign-in did not complete. Please try again.'],
  ['token_exchange_failed', 'We could not confirm that account. Please try again in a moment.'],
  [
    'failed_to_retrieve_user',
    'We could not read your details from that account. Please try again, or sign in with your email and password.',
  ],
  [
    'could_not_process_user',
    'We could not sign you in that way. Please sign in with your email and password, or contact support.',
  ],
  [
    'provider_unavailable',
    'That sign-in option is not available right now. Please use another way to sign in.',
  ],
  [
    'provider_unreachable',
    'That sign-in option did not respond. Please try again in a moment.',
  ],
  [
    'identity_unverified',
    'We could not confirm that account. Please try again, or use another way to sign in.',
  ],
  [
    'sign_in_not_linked',
    'That account is not connected to your portal account yet. Open your invitation link and choose the same sign-in option there, or sign in with your email and password.',
  ],
  [
    'provider_not_allowed',
    'That sign-in option cannot be used for this account. Please sign in the way you usually do.',
  ],
  [
    'invitation_invalid',
    'That invitation link is no longer valid. Please contact us for a new one.',
  ],
  // A staff account signed in with an account outside the company's own (#326).
  [
    'staff_domain_required',
    'Staff sign in with their work account only. Please choose your work account and try again.',
  ],
  // The sign-in would have created an account but the privacy notice or the Terms and
  // Conditions are not in force (production with only drafts): nothing was created.
  [
    'documents_unavailable',
    'Your account cannot be activated right now. Please try again later or contact us.',
  ],
]);

const SIGN_IN_ERROR_FALLBACK =
  'That sign-in did not complete. Please try again, or sign in with your email and password.';

/** The sentence the login banner shows for a failure slug. Anything unmapped
 *  falls back rather than leaking a new slug to the page. */
export function signInErrorCopy(slug: string): string {
  return SIGN_IN_ERROR_COPY.get(slug) ?? SIGN_IN_ERROR_FALLBACK;
}

const START_FAILED =
  'We could not open that sign-in option. Please try again, or use another way to sign in.';

/**
 * The sentence shown when a sign-in could not be started. A request that never
 * got an answer keeps the shared connection wording, because retrying is then
 * the right move. Whatever the server did answer is not shown: what it says
 * here is written for us (which field, which limit), not for the person.
 */
export function signInStartFailureCopy(err: unknown): string {
  if (axios.isAxiosError(err) && !err.response) {
    return getApiErrorMessage(err, START_FAILED);
  }
  return START_FAILED;
}

/**
 * What the page a provider returns the browser to hands the login page when the
 * account still owes its second factor. It travels as router navigation state
 * only: the challenge's `mfa_token` is a single-use credential, so it never goes
 * in the URL, in browser storage or in the auth store.
 */
export interface SignInHandoff {
  mfaChallenge: MfaChallengeResponse;
}

export function challengeHandoff(challenge: MfaChallengeResponse): SignInHandoff {
  return { mfaChallenge: challenge };
}

const MFA_METHODS: ReadonlySet<unknown> = new Set<MfaMethod>(['totp', 'recovery_code', 'webauthn']);

/**
 * Read a handed-over challenge back out of navigation state. That state is
 * untyped and anything may have navigated to /login, so only a complete
 * challenge is accepted — otherwise null, and the page shows its normal form.
 */
export function readChallengeHandoff(state: unknown): MfaChallengeResponse | null {
  if (typeof state !== 'object' || state === null) return null;
  const candidate = (state as Partial<SignInHandoff>).mfaChallenge as
    | Partial<MfaChallengeResponse>
    | null
    | undefined;
  if (typeof candidate !== 'object' || candidate === null) return null;
  if (candidate.mfa_required !== true) return null;
  if (typeof candidate.mfa_token !== 'string' || candidate.mfa_token === '') return null;
  if (!Array.isArray(candidate.methods)) return null;
  // The backend lists the factors in the order it prefers; keep that order.
  const methods = (candidate.methods as unknown[]).filter((method): method is MfaMethod =>
    MFA_METHODS.has(method),
  );
  if (methods.length === 0) return null;
  return { mfa_required: true, mfa_token: candidate.mfa_token, methods };
}
